import { merge } from 'ts-deepmerge';
import { SEPARATOR } from './constants/strings';
import type { Recompose, Ru } from './types.types';

export function recomposeObjectUrl<T>(shape: string, value: T) {
  const obj: Ru = {};
  if (shape.length <= 0) return obj;

  const keys = shape.split(SEPARATOR);
  if (keys.length === 1) {
    const key = keys.shift();
    obj[key!] = value;
  } else {
    const key = keys.shift();
    obj[key!] = recomposeObjectUrl(keys.join(SEPARATOR), value);
  }

  return obj;
}

/**
 *  Recompose a flatten object 
 *  @example
 * 
 *  { 'data.age': 10, 'human.login': 'login' }
 *  will become
 *  {
      data: {
        age: 10,
      },
      human: {
        login: 'login',
      },
    }
 *  @remark   
 *  The generated typescript type takes too much ressources

 *  @todo
    Add type to the return
 */
type Recompose_F = <const T extends Ru>(shape: T) => Recompose<T>;
type _Recompose_F = (shape: any) => any;
type _Recompose2_F = <T extends Ru>(shape: T) => Recompose<T>;

export type Recomposer = _Recompose2_F & {
  strict: Recompose_F;
  low: _Recompose_F;
};

const _recompose: _Recompose_F = shape => {
  const entries = Object.entries(shape);
  if (entries.length === 0) return {};
  const arr: any[] = [];
  entries.forEach(([key, value]) => {
    arr.push(recomposeObjectUrl(key, value));
  });
  return _recompose2(merge(...arr));
};

const _recompose2: _Recompose_F = shape => {
  const mustReturn =
    Array.isArray(shape) || typeof shape !== 'object' || shape === null;
  if (mustReturn) return shape;

  const entries = Object.entries(shape).sort(([a], [b]) =>
    a.localeCompare(b),
  );

  const isEmpty = entries.length === 0;
  if (isEmpty) return {};

  const isArray = entries.every(
    ([key]) => key.startsWith('[') && key.endsWith(']'),
  );
  if (isArray) {
    const arr: any[] = [];
    entries.forEach(([key, value]) => {
      const index = parseInt(key.slice(1, -1), 10);
      arr[index] = _recompose2(value);
    });
    return arr;
  }

  return entries.reduce((acc, [key, value]) => {
    acc[key] = _recompose2(value);
    return acc;
  }, {} as any);
};

export const recompose: Recomposer = shape => _recompose(shape);
recompose.low = _recompose;
recompose.strict = _recompose;
